// src/components/organizer/events/OrganizerEventsList.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  fetchOrganizerEvents,
  deleteEvent
} from "@/store/slices/organizerSlice";
import { 
  Plus, Search, Calendar, MapPin, Users, Eye, Trash2, Pencil, ShieldCheck
} from "lucide-react";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";
import EventFilters from "@/components/common/EventFilters";
import Pagination from "@/components/common/Pagination";
import useFilterParams from "@/hooks/useFilterParams";
import { toast } from "react-toastify";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "sponsor_listed", label: "Sponsor Listed" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const DEFAULT_FILTERS = {
  search: "",
  status: "",
  event_type: "",
  component: "",
  city: "",
  startDate: "",
  endDate: "",
  sort: "recent"
};

const OrganizerEventsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { events, isLoading, eventsPagination, organizerComponentCounts } = useSelector((state) => state.organizer);
  
  const [filters, handleFilterChange] = useFilterParams(DEFAULT_FILTERS);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const params = {
      page,
      limit,
      ...filters
    };
    
    // Remove empty strings
    Object.keys(params).forEach(key => {
      if (params[key] === "") {
        delete params[key];
      }
    });

    dispatch(fetchOrganizerEvents(params));
  }, [dispatch, filters, page, limit]);

  const handleDelete = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
      try {
        await dispatch(deleteEvent(eventId)).unwrap();
        toast.success("Event deleted successfully");
      } catch (error) {
        toast.error(error.message || "Failed to delete event");
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { tone: "info", label: "Submitted" },
      sponsor_listed: { tone: "info", label: "Sponsor Listed" },
      published: { tone: "success", label: "Published" },
      archived: { tone: "danger", label: "Archived" },
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return <Badge tone={config.tone} size="sm">{config.label}</Badge>;
  };

  const canEdit = (event) => {
    return event.status === "submitted" || event.edit_override?.granted;
  };

  const getActionButtons = (event) => {
    const hasEditOverride = event.edit_override?.granted && event.status !== "submitted";

    switch (event.status) {
      case "submitted":
      case "sponsor_listed":
        return (
          <>
            <Button
              variant="outline"
              size="xs"
              onClick={() => navigate(`/organizer/events/${event._id}`)}
              className="dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              View
            </Button>
            {event.status === "submitted" ? (
              <button
                onClick={() => handleDelete(event._id)}
                className="p-1.5 flex items-center justify-center text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Delete Event"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : hasEditOverride ? (
              <button
                onClick={() => navigate(`/organizer/events/${event._id}/edit`)}
                className="p-1.5 flex items-center justify-center text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                title="Edit Event (Admin Authorized)"
              >
                <Pencil className="w-4 h-4" />
              </button>
            ) : (
              <div className="w-[28px]"></div>
            )}
          </>
        );
      case "published":
        return (
          <>
            <Button
              variant="outline"
              size="xs"
              onClick={() => navigate(`/organizer/events/${event._id}`)}
            >
              View
            </Button>
            {hasEditOverride ? (
              <button
                onClick={() => navigate(`/organizer/events/${event._id}/edit`)}
                className="p-1.5 flex items-center justify-center text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                title="Edit Event (Admin Authorized)"
              >
                <Pencil className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => navigate(`/organizer/events/${event._id}/registrations`)}
                className="p-1.5 flex items-center justify-center text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/40 rounded-lg transition-colors"
                title="See Registrations"
              >
                <Users className="w-4 h-4" />
              </button>
            )}
          </>
        );
      case "archived":
        return (
          <>
            <Button
              variant="outline"
              size="xs"
              onClick={() => navigate(`/organizer/events/${event._id}`)}
            >
              View
            </Button>
            <div className="w-[28px]"></div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            My Events
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage and track your events
          </p>
        </div>
        <Link to="/organizer/events/create">
          <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </Link>
      </div>

      <EventFilters
        filters={filters}
        onChange={handleFilterChange}
        componentCounts={organizerComponentCounts}
        statusOptions={STATUS_OPTIONS}
      />

      {/* Events Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Loading events...
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {Object.values(filters).some(Boolean) ? "No events match your filters" : "No events yet"}
            </p>
            {!Object.values(filters).some(Boolean) && (
              <Link to="/organizer/events/create">
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Create your first event
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Registrations
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {events.map((event) => (
                  <tr 
                    key={event._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
                            {event.title}
                          </p>
                          {event.location?.city && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location.city}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(event.start_date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(event.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {event.registration_count || 0}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {getActionButtons(event)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Pagination */}
      {!isLoading && events.length > 0 && eventsPagination && (
        <Pagination
          page={eventsPagination.page}
          totalPages={eventsPagination.pages}
          onChange={(p) => setPage(p)}
        />
      )}
    </div>
  );
};

export default OrganizerEventsList;
